// src/components/pages/ProfilePage.jsx
import React, { useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Button,
  Image,
  Separator,
  Icon,
  Grid,
  GridItem,
} from "@chakra-ui/react";
import { useParams, Link as RouterLink } from "react-router-dom";
import { companies } from "../../data/companies";
import {
  Phone,
  Mail,
  Globe,
  Star,
  CheckCircle,
  ArrowLeft,
  MapPin,
  Calendar,
  DollarSign,
  Info,
} from "lucide-react";

const SPECIALTY_LABELS = {
  individual_1040: "Individuals / 1040",
  small_business: "Small Business",
  business: "Business",
  complex_corporate: "Complex Corporate",
  payroll_941: "Payroll 941",
  truckers_1099: "Truckers / 1099",
};

export default function ProfilePage() {
  const params = useParams();
  const routeId = (params.slug || params.key || "").toLowerCase();

  // Find by slug OR key (react-snap friendly; no useLocation)
  const company = useMemo(() => {
    return (
      companies.find(
        (c) =>
          String(c.slug || "").toLowerCase() === routeId ||
          String(c.key || "").toLowerCase() === routeId
      ) || null
    );
  }, [routeId]);

  const sub = { base: "gray.600", _dark: "gray.300" };
  const cardBg = { base: "white", _dark: "gray.800" };
  const border = { base: "gray.200", _dark: "whiteAlpha.300" };

  if (!company) {
    return (
      <Container maxW="6xl" py={{ base: 10, md: 16 }}>
        <Stack spacing={6}>
          <Button
            as={RouterLink}
            to="/tax-relief-companies"
            leftIcon={<Icon as={ArrowLeft} />}
          >
            Back to companies
          </Button>
          <Heading size="lg">Company not found</Heading>
          <Text color={sub}>
            We couldn’t find a provider for “{routeId}”. Try the list again.
          </Text>
        </Stack>
      </Container>
    );
  }

  // Accent color (fallback to blue.600)
  const accentColor = company.accentColor || "blue.600";
  const accentGrad = `linear(to-r, ${accentColor}, teal.400)`;

  // Stars
  const rating = Number(company.ratingAggregate) || 0;
  const stars = Array.from({ length: 5 }, (_, i) => {
    const filled = i < Math.round(rating);
    return (
      <Icon
        key={i}
        as={Star}
        boxSize="18px"
        color={
          filled
            ? { base: "yellow.500", _dark: "yellow.300" }
            : { base: "gray.300", _dark: "gray.600" }
        }
        style={filled ? { fill: "currentColor" } : {}}
      />
    );
  });

  // Pricing text helper
  function pricingText(p) {
    if (!p) return null;
    const parts = [];
    if (p.model) parts.push(p.model === "flat" ? "Flat fee" : p.model);
    if (Array.isArray(p.range) && p.range.length === 2) {
      parts.push(
        `$${Number(p.range[0]).toLocaleString()}–$${Number(
          p.range[1]
        ).toLocaleString()}`
      );
    }
    if (p.freeConsult) parts.push("Free consult");
    return parts.join(" • ");
  }

  // Pick best phone (callrail if present)
  const primaryPhone = company.callrailNumber || company.phone || null;

  return (
    <Box>
      {/* Header band with subtle accent bar */}
      <Box
        bg={{ base: "gray.50", _dark: "gray.900" }}
        borderBottomWidth="1px"
        py={{ base: 8, md: 12 }}
        position="relative"
      >
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          h="6px"
          bgGradient={accentGrad}
        />
        <Container maxW="6xl" position="relative" zIndex={1}>
          <HStack
            justify="space-between"
            align="flex-start"
            spacing={6}
            wrap="wrap"
          >
            <HStack spacing={5} align="center">
              {company.logo ? (
                <Image
                  src={company.logo}
                  alt={company.name}
                  w="72px"
                  h="72px"
                  objectFit="contain"
                />
              ) : null}
              <Stack spacing={1}>
                <HStack spacing={3} align="center">
                  <Heading size="lg">{company.name}</Heading>
                  {(company.featured || company.badgeText) && (
                    <Badge
                      bgGradient={accentGrad}
                      color="white"
                      rounded="md"
                      px={2}
                      py={0.5}
                      display="inline-flex"
                      alignItems="center"
                      gap="1"
                    >
                      <Icon as={Info} boxSize="14px" />
                      {company.badgeText || "Featured"}
                    </Badge>
                  )}
                </HStack>
                {company.tagline ? (
                  <Text color={sub}>{company.tagline}</Text>
                ) : null}
                <HStack spacing={1}>{stars}</HStack>
                <Text color={sub} fontSize="sm">
                  {company.reviewCount} reviews • {rating.toFixed(1)} / 5
                </Text>
              </Stack>
            </HStack>

            <HStack spacing={3}>
              {primaryPhone ? (
                <Button
                  as="a"
                  href={`tel:${primaryPhone}`}
                  leftIcon={<Icon as={Phone} />}
                  bgGradient={accentGrad}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                >
                  Call
                </Button>
              ) : null}
              {company.inquiryEmail ? (
                <Button
                  as="a"
                  href={`mailto:${company.inquiryEmail}`}
                  variant="outline"
                  leftIcon={<Icon as={Mail} />}
                >
                  Email
                </Button>
              ) : null}
              {company.links?.schedule ? (
                <Button
                  as="a"
                  href={company.links.schedule}
                  target="_blank"
                  rel="noopener"
                  variant="outline"
                  leftIcon={<Icon as={Calendar} />}
                >
                  Schedule
                </Button>
              ) : null}
              {company.siteUrl ? (
                <Button
                  as="a"
                  href={company.siteUrl}
                  target="_blank"
                  rel="noopener"
                  colorScheme="blue"
                  leftIcon={<Icon as={Globe} />}
                >
                  Visit Site
                </Button>
              ) : null}
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Body: sticky sidebar + flowing content */}
      <Container maxW="6xl" py={{ base: 10, md: 14 }}>
        <Grid templateColumns={{ base: "1fr", lg: "320px 1fr" }} gap={8}>
          {/* Sidebar */}
          <GridItem>
            <Box position={{ lg: "sticky" }} top={{ lg: 6 }}>
              <Stack spacing={5}>
                {/* Quick info card */}
                <Box
                  borderWidth="1px"
                  borderColor={border}
                  bg={cardBg}
                  rounded="lg"
                  p={5}
                >
                  <Heading size="sm" mb={3}>
                    At a glance
                  </Heading>
                  <Stack spacing={2}>
                    {company.coverage ? (
                      <HStack spacing={2}>
                        <Heading>Coverage</Heading>
                        <Text color={sub}>{company.coverage}</Text>
                      </HStack>
                    ) : null}
                    {Array.isArray(company.languages) &&
                    company.languages.length ? (
                      <HStack spacing={2} align="start">
                        <Heading>Language</Heading>
                        <HStack spacing={1} wrap="wrap">
                          {company.languages.map((lang) => (
                            <Badge
                              key={lang}
                              colorScheme="purple"
                              variant="subtle"
                            >
                              {lang}
                            </Badge>
                          ))}
                        </HStack>
                      </HStack>
                    ) : null}
                    {company.pricing ? (
                      <HStack spacing={2} align="start">
                        <Heading>Pricing</Heading>
                        <HStack spacing={2}>
                          <Icon as={DollarSign} boxSize="16px" />
                          <Text color={sub}>
                            {pricingText(company.pricing)}
                          </Text>
                        </HStack>
                      </HStack>
                    ) : null}
                    {Array.isArray(company.locations) &&
                    company.locations.length ? (
                      <HStack spacing={2} align="start">
                        <Icon as={MapPin} boxSize="16px" />
                        <VStack align="start" spacing={1}>
                          {company.locations.map((loc) => (
                            <Text key={loc} color={sub} fontSize="sm">
                              {loc}
                            </Text>
                          ))}
                        </VStack>
                      </HStack>
                    ) : null}
                  </Stack>
                </Box>

                {/* Contact card */}
                <Box
                  borderWidth="1px"
                  borderColor={border}
                  bg={cardBg}
                  rounded="lg"
                  p={5}
                >
                  <Heading size="sm" mb={3}>
                    Contact
                  </Heading>
                  <Stack spacing={2}>
                    {company.phone ? <Text>Phone: {company.phone}</Text> : null}
                    {company.inquiryEmail ? (
                      <Text>Email: {company.inquiryEmail}</Text>
                    ) : null}
                    {company.siteUrl ? (
                      <Text>Website: {company.siteUrl}</Text>
                    ) : null}
                    {company.intake?.formUrl ? (
                      <Button
                        as="a"
                        href={company.intake.formUrl}
                        target="_blank"
                        rel="noopener"
                        mt={2}
                        bgGradient={accentGrad}
                        color="white"
                        _hover={{ opacity: 0.9 }}
                      >
                        Start Intake
                      </Button>
                    ) : null}
                  </Stack>
                </Box>

                <Button
                  as={RouterLink}
                  to="/tax-relief-companies"
                  variant="outline"
                  leftIcon={<Icon as={ArrowLeft} />}
                >
                  Back to companies
                </Button>
              </Stack>
            </Box>
          </GridItem>

          {/* Main column */}
          <GridItem>
            <Stack spacing={8}>
              {/* Review snippet / intro */}
              {company.reviewSnippet ? (
                <Box
                  borderWidth="1px"
                  borderColor={border}
                  bg={cardBg}
                  rounded="lg"
                  p={6}
                >
                  <Text color={sub} fontStyle="italic" fontSize="lg">
                    “{company.reviewSnippet}”
                  </Text>
                </Box>
              ) : null}

              {/* Specialties */}
              {(company.specialties || []).length ? (
                <Box>
                  <Heading size="md" mb={3}>
                    Focus areas
                  </Heading>
                  <HStack spacing={2} wrap="wrap">
                    {company.specialties.map((s) => (
                      <Badge
                        key={s}
                        colorScheme="blue"
                        variant="subtle"
                        py={1}
                        px={2}
                        rounded="md"
                      >
                        {SPECIALTY_LABELS[s] || s.replace(/_/g, " ")}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              ) : null}

              <Separator />

              {/* Services — interactive-ish cards */}
              {Array.isArray(company.services) && company.services.length ? (
                <Box>
                  <Heading size="md" mb={4}>
                    Services
                  </Heading>
                  <Stack spacing={4}>
                    {company.services.map((s, idx) => {
                      const svc = typeof s === "string" ? { name: s } : s || {};
                      const key = svc.code || svc.name || `svc-${idx}`;
                      return (
                        <Box
                          key={key}
                          borderWidth="1px"
                          borderColor={border}
                          bg={cardBg}
                          rounded="lg"
                          p={4}
                          _hover={{
                            shadow: "md",
                            transform: "translateY(-2px)",
                          }}
                          transition="all 0.15s ease"
                        >
                          <HStack justify="space-between" align="start">
                            <HStack spacing={3} align="start">
                              <Icon
                                as={CheckCircle}
                                boxSize="18px"
                                color={{
                                  base: "green.500",
                                  _dark: "green.300",
                                }}
                                style={{ marginTop: "2px" }}
                              />
                              <Stack spacing={0.5}>
                                <Heading size="sm">
                                  {svc.name || svc.code || "Service"}
                                </Heading>
                                {svc.short ? (
                                  <Text color={sub} fontSize="sm">
                                    {svc.short}
                                  </Text>
                                ) : null}
                              </Stack>
                            </HStack>
                            {typeof svc.startingAt === "number" ? (
                              <Badge colorScheme="blue" variant="solid">
                                from ${svc.startingAt.toLocaleString()}
                              </Badge>
                            ) : null}
                          </HStack>
                          {svc.details ? (
                            <Text mt={3} color={sub}>
                              {svc.details}
                            </Text>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>
              ) : null}
            </Stack>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}
